/**
 * agentIntent.ts — Semantisk intent-klassifisering via LLM
 *
 * Bruker backend API (/api/intent/classify) med Claude Haiku for å tolke
 * hva brukeren faktisk mener, i stedet for hardkodede ordlister.
 *
 * Flyten:
 * 1. Frontend kaller classifyAgentIntentAsync() som sender meldingen til backend
 * 2. Backend bruker Claude Haiku til å klassifisere semantisk
 * 3. Returnerer mode + confidence + reasoning
 * 4. Hvis confidence < 0.6, returnerer 'uncertain' slik at frontend kan spørre brukeren
 * 5. Hvis API feiler, faller tilbake til enkel lokal heuristikk
 */

export type AgentIntentMode = 'chat_reply' | 'light_agent' | 'full_agent'

export interface IntentClassification {
  mode: AgentIntentMode
  confidence: number
  reasoning: string
  method: 'llm' | 'fallback' | 'local_fallback'
  uncertain: boolean  // true when confidence < threshold
}

import { API_BASE } from '@/lib/config'

// Confidence threshold — below this, we ask the user to clarify
const UNCERTAINTY_THRESHOLD = 0.6

/**
 * Async LLM-based intent classification.
 * This is the primary classification method — calls the backend API.
 */
export async function classifyAgentIntentAsync(
  input: string,
  conversationContext?: Array<{ role: string; content: string }>,
  authToken?: string,
): Promise<IntentClassification> {
  const text = input.trim()
  if (!text) {
    return {
      mode: 'chat_reply',
      confidence: 1.0,
      reasoning: 'Tom melding',
      method: 'local_fallback',
      uncertain: false,
    }
  }

  // Quick local check for obvious greetings (avoid API call)
  if (isObviousGreeting(text)) {
    return {
      mode: 'chat_reply',
      confidence: 0.95,
      reasoning: 'Åpenbar hilsen/bekreftelse',
      method: 'local_fallback',
      uncertain: false,
    }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${API_BASE}/api/intent/classify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: text,
        conversation_context: conversationContext?.slice(-4) ?? [],
      }),
      signal: AbortSignal.timeout(3000), // 3 second timeout
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`)
    }

    const result = await response.json()
    const mode = result.mode as AgentIntentMode
    const confidence = result.confidence as number
    const reasoning = result.reasoning as string
    const method = result.method as 'llm' | 'fallback'

    return {
      mode,
      confidence,
      reasoning,
      method,
      uncertain: confidence < UNCERTAINTY_THRESHOLD,
    }
  } catch (err) {
    // API failed — use local fallback
    console.warn('[agentIntent] LLM classification failed, using local fallback:', err)
    return classifyAgentIntentLocal(text)
  }
}

/**
 * Synchronous local fallback classifier.
 * Used when the API is unavailable or for instant pre-classification.
 * This is a simplified version — not as smart as the LLM.
 */
export function classifyAgentIntent(input: string): AgentIntentMode {
  return classifyAgentIntentLocal(input.trim()).mode
}

function classifyAgentIntentLocal(text: string): IntentClassification {
  const normalized = text.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim()
  if (!normalized) {
    return { mode: 'chat_reply', confidence: 1.0, reasoning: 'Tom melding', method: 'local_fallback', uncertain: false }
  }

  // 1. Obvious greetings
  if (isObviousGreeting(normalized)) {
    return { mode: 'chat_reply', confidence: 0.95, reasoning: 'Hilsen/bekreftelse', method: 'local_fallback', uncertain: false }
  }

  const words = normalized.split(/\s+/).filter(Boolean)
  const wordCount = words.length

  // 2. Action signals — user wants something DONE
  const actionSignals = [
    'lag ', 'lage ', 'lager ', 'bygg ', 'bygge ', 'bygger ',
    'skriv ', 'skrive ', 'skriver ', 'opprett ', 'opprette ',
    'slett ', 'slette ', 'fiks ', 'fikse ', 'installer ', 'installere ',
    'sett opp', 'sette opp', 'set up',
    'finn ', 'finne ', 'sok ', 'soke ', 'søk ', 'søke ', 'undersok ', 'undersoke ', 'undersøk ', 'undersøke ',
    'hent ', 'hente ', 'sjekk ', 'sjekke ', 'let etter', 'ta a finne', 'ta å finne',
    'deploy ', 'deploye ', 'push til', 'pull fra',
    'send ', 'sende ', 'post ', 'poste ', 'publiser ', 'publisere ',
    'oppdater ', 'oppdatere ', 'endre ', 'flytt ', 'flytte ',
    'kopier ', 'kopiere ', 'last ned', 'laste ned',
    'konfigurer ', 'konfigurere ', 'implementer ', 'implementere ',
    'forbedre ', 'forbedrer ', 'gjor ', 'gjore ',
    'legg til', 'legge til', 'fjern ', 'fjerne ',
    'create ', 'build ', 'write ', 'delete ', 'fix ', 'install ', 'run ',
    'make me ', 'make a ', 'make an ', 'generate ', 'design ',
    'download ', 'configure ', 'implement ',
  ]
  if (actionSignals.some(s => normalized.includes(s))) {
    return { mode: 'full_agent', confidence: 0.75, reasoning: 'Handlingsverb detektert (lokal)', method: 'local_fallback', uncertain: false }
  }

  // 3. Advisory signals — user wants ADVICE
  const advisorySignals = [
    'hva synes du', 'hva tenker du', 'hva mener du', 'hva tror du',
    'ideer', 'tips', 'rad', 'advice', 'anbefaler', 'anbefaling',
    'vurdere', 'vurder', 'gjennomga', 'review', 'se over',
    'tanker om', 'suggestions', 'what do you think', 'what should',
    'how can', 'hva bor', 'hvordan kan vi', 'hvordan bor',
    'forklar', 'explain', 'oppsummer', 'summarize', 'beskriv', 'describe',
  ]
  if (advisorySignals.some(s => normalized.includes(s))) {
    return { mode: 'light_agent', confidence: 0.7, reasoning: 'Rådgivningssignal detektert (lokal)', method: 'local_fallback', uncertain: false }
  }

  // 4. Full agent context keywords
  const fullAgentKeywords = [
    'logg inn', 'login', 'nettleser', 'browser', 'terminal',
    'automatiser', 'automation', 'workflow', 'debug', 'feilsok',
    'fortsett', 'fortsette', 'prøv igjen', 'try again', 'resume', 'continue',
    'gjor det', 'gjore det', 'ja, gjor', 'ja gjor', 'start',
  ]
  if (fullAgentKeywords.some(k => normalized.includes(k))) {
    return { mode: 'full_agent', confidence: 0.65, reasoning: 'Agent-kontekstsignal (lokal)', method: 'local_fallback', uncertain: false }
  }

  // 5. Short questions → chat_reply
  if (wordCount <= 8 && normalized.endsWith('?')) {
    return { mode: 'chat_reply', confidence: 0.5, reasoning: 'Kort spørsmål (lokal)', method: 'local_fallback', uncertain: false }
  }

  // 6. Default: short = light, long = full
  if (wordCount <= 15) {
    return { mode: 'light_agent', confidence: 0.4, reasoning: 'Standardregel: kort melding (lokal)', method: 'local_fallback', uncertain: true }
  }
  return { mode: 'full_agent', confidence: 0.4, reasoning: 'Standardregel: lang melding (lokal)', method: 'local_fallback', uncertain: true }
}

/**
 * Check for obvious greetings that don't need LLM classification.
 */
function isObviousGreeting(text: string): boolean {
  const normalized = text.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim()
  const greetingPatterns = [
    /^(hei|hello|hi|hey|yo|god morgen|god kveld|god dag)[!.? ]*$/,
    /^(takk|thanks|thank you|supert|nice|ok|okei|greit|flott|bra)[!.? ]*$/,
    /^(ja|nei|jo|nope|yep|yes|no)[!.? ]*$/,
  ]
  return greetingPatterns.some(p => p.test(normalized))
}
